import {useEffect, useState} from "react";
import {CustomAny} from "types/global";

type Encoder<T> = {
  encode: (value: T) => string;
  decode: (value: string) => T;
}

export type LocalStore<T> = {
  key: string;
  set: (value: T | undefined | null) => void;
  get: () => T | null;
  remove: () => void;
}
const cache: { [key: string]: LocalStore<CustomAny> } = {};

export const localStore = <T>(
  namespace: string,
  key: string,
  encoder: Encoder<T>,
): LocalStore<T> => {
  const path = namespace ? `${namespace}:${key}` : key;

  if (!Object.hasOwn(cache, path)) {
    cache[path] = {
      key: path,
      set(value: T | undefined) {
        const encoded =
          value === undefined || value === null ? "" : encoder.encode(value);

        if (encoded === "") localStorage.removeItem(path);
        else localStorage.setItem(path, encoded);
      },
      get(): T | null {
        const value = localStorage.getItem(path);

        return value === null || value === undefined
          ? null
          : encoder.decode(value);
      },
      remove() {
        localStorage.removeItem(path);
      },
    };
  }

  return cache[path] as LocalStore<T>;
}

export const useStoreState = <T>(store: LocalStore<T>, defaultValue: T) => {
  const [value, setValue] = useState<T>(store.get() ?? defaultValue);

  useEffect(() => {
    store.set(value);
  }, [store, value]);

  return [value, setValue] as const;
}

export const jsonStore = <T>(namespace: string, key: string) => {
  return localStore<T>(namespace, key, {
    encode: JSON.stringify,
    decode: JSON.parse,
  });
}

export const stringStore = <T extends string = string>(
  namespace: string,
  key: string,
) => {
  return localStore<T>(namespace, key, {
    encode: value => value,
    decode: value => value as T,
  });
}

export const numberStore = (namespace: string, key: string) => {
  return localStore<number>(namespace, key, {
    encode: value => value.toString(),
    decode: value => Number(value),
  });
}

export const boolStore = (namespace: string, key: string) => {
  return localStore<boolean>(namespace, key, {
    encode: value => Number(value).toString(),
    decode: value => value === "1",
  });
}

/**
 * Safely saves a value to localStorage under the specified key.
 *
 * @param key - The localStorage key
 * @param value - The value to store
 */
export const setLocalStorageItem = <T>(key: string, value: T): void => {
  if (typeof window === "undefined") return; // SSR safety

  try {
    const serialized = JSON.stringify(value);
    localStorage.setItem(key, serialized);
  } catch (error) {
    console.error(`Error setting localStorage key "${key}":`, error);
  }
}

/**
 * Retrieves and deserializes a value from localStorage.
 *
 * @param key - The localStorage key
 * @param fallback - Fallback value if nothing is found or parsing fails
 * @returns The parsed value or the fallback
 */
export const getLocalStorageItem = <T>(
  key: string,
  fallback?: T,
): T | undefined => {
  if (typeof window === "undefined") return fallback;

  try {
    const item = localStorage.getItem(key);
    if (!item) return fallback;

    return JSON.parse(item) as T;
  } catch (error) {
    console.warn(`Error parsing localStorage key "${key}":`, error);
    return fallback;
  }
}

/**
 * Deletes a localStorage item by key.
 *
 * @param key - The key to remove
 */
export const removeLocalStorageItem = (key: string): void => {
  if (typeof window === "undefined") return;

  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Error removing localStorage key "${key}":`, error);
  }
}

/**
 * Clears all key/value pairs from the browser's localStorage.
 *
 * @remarks
 * This function will remove all data stored in localStorage for the current domain.
 * Use with caution, as this action is irreversible and will affect all users' data
 * stored in localStorage for the application.
 *
 * @example
 * // Clear all localStorage data
 * clearLocalStorage();
 */
export const clearLocalStorage = () => localStorage.clear();
